import { throwError, of, timer, BehaviorSubject, from } from 'rxjs';
import { retryWhen, mergeMap, tap, finalize, shareReplay, map, scan, defaultIfEmpty, catchError, switchMap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { JsonConvert, JsonProperty, JsonObject, Any, JsonConverter } from 'json2typescript';
import { __decorate } from 'tslib';
import { parseJSON, format, parse } from 'date-fns';

/* eslint-disable arrow-body-style */
/**
 * Marker class if we want to retry. Use it with throwError to indicate when you want to retry.
 * The retryStrategy will swallow this error and automatically retry by re-subscribing.
 */
class DoRetry {
    constructor(data) {
        this.data = data;
    }
}
/**
 * Waits until a given Task is finished and then emits the result and completes.
 *
 * If the Task is not finished it will be polled until it either completes, fails permanently or the maximum
 * number of retries is reached.
 *
 * If the task fails or maximum retries is reached a RetryError will be thrown, that includes the latest
 * result of the Task.
 */
const waitForTask = ({ maxRetries = 10, intervalMs = 500, backoff = 'flat', log = false } = {}) => {
    return (source) => {
        return source.pipe(mapStatusToError(), retryWhen(retryStrategy({ maxRetries, intervalMs, backoff, log })));
    };
};
/** Map the task status to an error for retry, or forward it has failed */
const mapStatusToError = () => (source) => source.pipe(mergeMap((task) => {
    if (task.status === 'PROCESSING') {
        return throwError(new DoRetry(task));
    }
    if (task.status === 'FAILED') {
        return throwError({
            reason: 'FAILED_PERMANENTLY',
            data: task
        });
    }
    return of(task);
}));
/**
 * Used in conjunction with `retryWhen` to perform automatic retries. Example:
 *
 *   this.httpClient.get(url).pipe(
 *     mergeMap(response => {
 *      if (response.something) {
 *        return throwError(new DoRetry()); // will be swallowed
 *      } else if (response.somethingElse) {
 *        return throwError(new Error('This will propagate though')):
 *      } else {
 *        return of(response);
 *      }
 *     }),
 *     retryWhen(retryStrategy({maxRetries, intervalMs, backoff, log}))
 *   )
 */
const retryStrategy = (options) => (attempts) => {
    return attempts.pipe(mergeMap((error, retryAttempt) => {
        if (error instanceof DoRetry) {
            if (retryAttempt >= options.maxRetries) {
                if (options.log) {
                    console.warn(`Maximum number of ${options.maxRetries} retries reached!`);
                }
                return throwError({
                    data: error.data,
                    reason: 'MAXIMUM_RETRIES_REACHED'
                });
            }
            const delayMs = options.backoff === 'flat' ?
                options.intervalMs :
                exponentialBackoff(retryAttempt, options.intervalMs);
            if (options.log) {
                console.log(`Retry attempt ${retryAttempt} of ${options.maxRetries}. Next retry in ${delayMs}ms`);
            }
            return timer(delayMs);
        }
        // rethrow any other error
        return throwError(error);
    }));
};
// eslint-disable-next-line arrow-body-style
const exponentialBackoff = (iteration, initialInterval, maxInterval = Infinity) => {
    return Math.min(Math.pow(2, iteration) * initialInterval, maxInterval);
};

/**
 * Automagic caching for requests.
 *
 * Keeps a cache of already received data as well as any in-flight requests so data is
 * never fetched twice even in concurrent scenarios.
 */
class AutoCache {
    constructor() {
        this.inflight = {};
        this.cached = {};
    }
    doCache(key, refresh = false) {
        return (source) => {
            if (key in this.cached && !refresh) {
                return of(this.cached[key]);
            }
            if (key in this.inflight && !refresh) {
                return this.inflight[key];
            }
            return this.inflight[key] = source.pipe(tap(data => this.cached[key] = data), finalize(() => {
                if (key in this.inflight) {
                    delete this.inflight[key];
                }
            }), shareReplay(1));
        };
    }
}

class PageLinks {
}
class Page {
    constructor() {
        this.number = 0;
        this.size = 0;
        this.totalElements = 0;
        this.totalPages = 0;
    }
}
class PagedResult {
    constructor() {
        this.page = new Page();
        this._links = new PageLinks();
        this._embedded = new Array();
    }
}

const URL_REGEX = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm;
class UriUtils {
    static open(uri) {
        if (!URL_REGEX.test(uri)) {
            throw Error('Can\'t open invalid uri \'' + uri + '\'');
        }
        if (uri.startsWith('http://') || uri.startsWith('https://')) {
            window.open(uri);
        }
        else {
            window.open('https://' + uri);
        }
    }
    static toUri(segments) {
        return segments.join('/');
    }
}

/* eslint-disable no-underscore-dangle */
class HttpSort {
    constructor(key, direction) {
        this.key = key;
        this.direction = direction;
    }
}
class HalResourceService {
    constructor(jsonCollectionName, http, classReference, baseUrl) {
        this.jsonCollectionName = jsonCollectionName;
        this.http = http;
        this.classReference = classReference;
        this.baseUrl = baseUrl;
        this.jsonConvert = new JsonConvert();
        this.resourceUpdates$ = new BehaviorSubject(undefined);
    }
    get updates() {
        return this.resourceUpdates$.asObservable();
    }
    findPage(url, args = {}) {
        let httpParams = this.initParams(args.params);
        httpParams = this.addSortParams(httpParams, args.sort);
        httpParams = this.addPageParam(httpParams, args.page);
        httpParams = this.addSizeParam(httpParams, args.size);
        return this.http.get(this.composeRequestUrl(url), { params: httpParams }).pipe(map(res => this.responseToPagedResult(res)));
    }
    /**
     * Loads all pages sequentially.
     * This method emits once, when all pages have been downloaded.
     *
     * Usually findAllPagesParallel() is a better alternative since downloading
     * multiple pages in parallel is faster and the known pages can be used
     * before all have been downloaded.
     */
    findAllPages(url, args = {}) {
        const size = args.size ? args.size : 2000;
        let httpParams = this.initParams(args.params);
        httpParams = this.addSortParams(httpParams, args.sort);
        httpParams = this.addPageParam(httpParams, 0);
        httpParams = this.addSizeParam(httpParams, size);
        return this.findNextPageAndAddResults(this.composeRequestUrl(url) + '?' + httpParams.toString(), []);
    }
    /**
     * Loads all pages using a defined number of parallel downloads.
     * When a new page is available, the complete result consisting of all known
     * pages is emitted.
     *
     * Usually this method performs better than findAllPages() which does a
     * sequential download and emits only once.
     *
     * If a page cannot be loaded, an error is printed to the console, but not
     * emitted by the method. An empty page is assumed then.
     */
    findAllPagesParallel(url, args = {}) {
        return this.createPageRequest(url, 0, args.sort, args.size, args.params).pipe(mergeMap((firstPage) => {
            if (firstPage.page.totalPages <= 1) {
                return of(firstPage._embedded ?? []);
            }
            const requests = this.createAllPageRequests(firstPage, url, args.sort, args.size, args.params);
            return from(requests).pipe(
            // request all pages with a pool of parallel requests
            mergeMap((observable) => observable, args.parallelDownloads ?? 4), 
            // merge all pages preserving their order
            scan((acc, value) => {
                acc[value.index] = value.value;
                return acc;
            }, Array(requests.length)), 
            // flatten page array
            map((pages) => pages.reduce((acc, val) => acc.concat(val), [])), 
            // make sure to return even if observables array is empty
            defaultIfEmpty([]), catchError((error) => {
                console.log(error);
                return of([]);
            }));
        }));
    }
    findAll(url = undefined, args = {}) {
        let httpParams = this.initParams(args.params);
        httpParams = this.addSortParams(httpParams, args.sort);
        return this.http.get(this.composeRequestUrl(url), { params: httpParams }).pipe(map((res) => {
            if (!res._embedded) {
                return [];
            }
            return this.jsonConvert.deserializeArray(this.getCollection(res), this.classReference);
        }));
    }
    findAllByLink(link, params, sort) {
        return this.findAll(link.href, { params, sort });
    }
    findOne(uri) {
        return this.findOneByUri(uri);
    }
    findOneByUri(uri) {
        return this.http.get(uri).pipe(map(res => {
            if (res._embedded && this.hasCollection(res)) {
                throw Error('Multiple objects returned, but single model expected when loading link.');
            }
            else {
                return this.jsonConvert.deserializeObject(res, this.classReference);
            }
        }));
    }
    findOneByRelativeUrl(url) {
        return this.http.get(this.composeRequestUrl(url)).pipe(map(res => {
            if (res._embedded && this.hasCollection(res)) {
                throw Error('Multiple objects returned, but single model expected when loading link.');
            }
            else {
                return this.jsonConvert.deserializeObject(res, this.classReference);
            }
        }));
    }
    findOneById(id) {
        if (!this.baseUrl) {
            throw new Error('baseUri must be set');
        }
        return this.findOne(UriUtils.toUri([this.baseUrl, id]));
    }
    findOneByLink(link) {
        return this.findOne(link.href);
    }
    create(url, args = {}) {
        return this.http.post(this.composeRequestUrl(url), args.fields)
            .pipe(map(response => this.jsonConvert.deserializeObject(response, this.classReference)), tap(result => this.publishResourceUpdate(result)));
    }
    /**
     * A version of create() that returns the full HttpResponse and does not
     * process the reply.
     */
    createRaw(url, args = {}) {
        return this.http.post(this.composeRequestUrl(url), args.fields, { observe: 'response' });
    }
    patch(args = {}) {
        const url = args.resource?.selfLinkHref ?? args.uri;
        if (!url) {
            throw Error('Unable to perform patch request. Either resource or uri must be set.');
        }
        return this.http.patch(url, args.fields).pipe(map(response => this.jsonConvert.deserializeObject(response, this.classReference)), tap(result => this.publishResourceUpdate(result)));
    }
    put(args = {}) {
        const fieldsToPut = args.fields ?? this.putableFieldsFromResource(args.resource);
        if (!fieldsToPut) {
            throw Error('Unable to perform put request. Either args.resource or ars.fields must be set.');
        }
        const url = args.resource?.selfLinkHref ?? args.uri;
        if (!url) {
            throw Error('Unable to perform put request. Either args.resource or args.uri must be set.');
        }
        return this.http.put(url, fieldsToPut).pipe(map(response => this.jsonConvert.deserializeObject(response, this.classReference)), tap(result => this.publishResourceUpdate(result)));
    }
    delete(args = {}) {
        const url = args.resource?.selfLinkHref ?? args.uri;
        if (!url) {
            throw Error('Unable to perform delete request. Either resource or uri must be set.');
        }
        return this.http.delete(url);
    }
    initParams(params) {
        return params ? new HttpParams({ fromObject: params }) : new HttpParams();
    }
    addSortParams(httpParams, httpSort) {
        if (httpSort) {
            const sortArray = Array.isArray(httpSort) ? httpSort : [httpSort];
            for (const sort of sortArray) {
                httpParams = httpParams.append('sort', sort.key + ',' + sort.direction);
            }
        }
        return httpParams;
    }
    addPageParam(httpParams, page) {
        return page !== undefined ? httpParams.set('page', page.toString()) : httpParams;
    }
    addSizeParam(httpParams, size) {
        return size !== undefined ? httpParams.set('size', size.toString()) : httpParams;
    }
    responseToPagedResult(response) {
        const pagedResult = new PagedResult();
        pagedResult._links = response._links;
        if (response._embedded) {
            try {
                pagedResult._embedded = this.jsonConvert.deserializeArray(this.getCollection(response), this.classReference);
            }
            catch (error) {
                console.error(error);
                pagedResult._embedded = [];
            }
        }
        pagedResult.page = response.page;
        return pagedResult;
    }
    findNextPageAndAddResults(next, results) {
        return this.http.get(next).pipe(switchMap(response => {
            const pagedResult = this.responseToPagedResult(response);
            results.push(...pagedResult._embedded);
            if (pagedResult._links.next) {
                return this.findNextPageAndAddResults(pagedResult._links.next.href, results);
            }
            return of(results);
        }));
    }
    publishResourceUpdate(resource) {
        this.resourceUpdates$.next(resource);
    }
    hasCollection(container) {
        return !!this.jsonCollectionName && !!container._embedded[this.jsonCollectionName];
    }
    getCollection(container) {
        if (!this.jsonCollectionName) {
            throw new Error('jsonCollectionName needs to be set on instance creation.');
        }
        if (!container._embedded[this.jsonCollectionName]) {
            throw new Error(`The HAL resource does not have the expected JSON collection "${this.jsonCollectionName}".
      Did you mean one of the following? ${Object.keys(container._embedded)}`);
        }
        return container._embedded[this.jsonCollectionName];
    }
    composeRequestUrl(url) {
        if (url === undefined) {
            return this.baseUrl;
        }
        if (url.indexOf('http') === 0 || url.indexOf('//') === 0) {
            return url;
        }
        let requestUrl = this.baseUrl;
        if (url.length > 0 && !url.startsWith('/')) {
            requestUrl += '/';
        }
        return `${requestUrl}${url}`;
    }
    createPageRequest(url, page, sort, pageSize, params) {
        return this.findPage(url, { params: { page, size: pageSize, ...params }, sort });
    }
    createAllPageRequests(firstPage, url, sort, pageSize, params) {
        return Array(firstPage.page.totalPages).fill(undefined).map((value, index) => {
            if (index === 0) {
                return of({ index: 0, value: firstPage._embedded }); // we already know the first page
            }
            return this.createPageRequest(url, index, sort, pageSize, params).pipe(map((page) => ({ index, value: page._embedded })), catchError((error) => {
                console.log(error);
                return of({ index, value: [] });
            }));
        });
    }
    putableFieldsFromResource(resource) {
        if (!resource) {
            return undefined;
        }
        const fields = new JsonConvert().serializeObject(resource.valueOf());
        delete fields._links;
        delete fields._embedded;
        delete fields.id;
        return fields;
    }
}

let Link = class Link {
    constructor() {
        this.href = '';
    }
};
__decorate([
    JsonProperty('href', String)
], Link.prototype, "href", void 0);
Link = __decorate([
    JsonObject('Link')
], Link);
/**
 * json2typesript needs fields to be actually set to "something" (even undefined) so they
 * exists at the class at runtime. To avoid having to use some random stuff we us "undefined"
 * but cast it as "any" so it can be assigned to any field (even if the field is not defined as "| undefined").
 */
const INIT_BY_JSON_2_TS = undefined;
let HalResource = class HalResource {
    constructor() {
        this._links = undefined;
        this._embedded = undefined;
    }
    get selfLink() {
        const link = this._links?.self;
        if (!link) {
            throw new Error('No selfLink provided.');
        }
        return link;
    }
    get selfLinkHref() {
        return this.selfLink.href;
    }
    getLink(name) {
        return this.getLinkByName(name);
    }
    getLinkArray(name) {
        const link = this.getLinkByName(name);
        if (!link) {
            return undefined;
        }
        return (Array.isArray(link) ? link : [link]);
    }
    getEmbedded(name, classReference) {
        const embeddedField = this.getEmbeddedByName(name);
        if (embeddedField) {
            try {
                return new JsonConvert().deserializeObject(embeddedField, classReference);
            }
            catch (e) {
                throw new Error(`Error converting ${name} in _embedded:` + e);
            }
        }
        return undefined;
    }
    getOptionalEmbedded(name, classReference) {
        const embeddedField = this.getEmbeddedByName(name);
        if (embeddedField) {
            try {
                return new JsonConvert().deserializeObject(embeddedField, classReference);
            }
            catch (e) {
                throw new Error(`Error converting ${name} in _embedded:` + e);
            }
        }
        return undefined;
    }
    getEmbeddedArray(name, classReference) {
        const embeddedField = this.getEmbeddedByName(name);
        if (embeddedField) {
            try {
                return new JsonConvert().deserializeArray(embeddedField, classReference);
            }
            catch (e) {
                throw new Error(`Error converting ${name} in _embedded:` + e);
            }
        }
        return undefined;
    }
    getLinkByName(name) {
        if (!this._links) {
            return undefined;
        }
        return this._links[name];
    }
    getEmbeddedByName(name) {
        if (!this._embedded) {
            return undefined;
        }
        return this._embedded[name];
    }
};
__decorate([
    JsonProperty('_links', Any, true)
], HalResource.prototype, "_links", void 0);
__decorate([
    JsonProperty('_embedded', Any, true)
], HalResource.prototype, "_embedded", void 0);
HalResource = __decorate([
    JsonObject('HalResource')
], HalResource);
let IdHalResource = class IdHalResource extends HalResource {
    constructor() {
        super(...arguments);
        this.id = undefined;
    }
};
__decorate([
    JsonProperty('id', String, true)
], IdHalResource.prototype, "id", void 0);
IdHalResource = __decorate([
    JsonObject('IdHalResource')
], IdHalResource);

let DateConverter = class DateConverter {
    serialize(date) {
        return date.toISOString();
    }
    deserialize(dateString) {
        return parseJSON(dateString);
    }
};
DateConverter = __decorate([
    JsonConverter
], DateConverter);
let LocalDateConverter = class LocalDateConverter {
    serialize(date) {
        return format(date, 'yyyy-MM-dd');
    }
    deserialize(dateString) {
        return parse(dateString, 'yyyy-MM-dd', new Date());
    }
};
LocalDateConverter = __decorate([
    JsonConverter
], LocalDateConverter);

let TimelessDateConverter = class TimelessDateConverter {
    serialize(date) {
        return format(date, 'yyyy-MM-dd');
    }
    deserialize(dateString) {
        return parse(dateString, 'yyyy-MM-dd', new Date());
    }
};
TimelessDateConverter = __decorate([
    JsonConverter
], TimelessDateConverter);

/*
 * Public API Surface of tng-angular-common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AutoCache, DateConverter, DoRetry, HalResource, HalResourceService, HttpSort, INIT_BY_JSON_2_TS, IdHalResource, Link, LocalDateConverter, Page, PageLinks, PagedResult, TimelessDateConverter, UriUtils, retryStrategy, waitForTask };
